import React, { useState, useCallback } from 'react';
import { registerToastCallbacks } from '../../../Utils/Functions/toastService.js';
import './Toast.css';

const Toast = ({ id, message, type, onClose }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, 7000);

    return () => clearTimeout(timer);
  }, [id, onClose]);

  return (
    <div className={`toast toast-${type}`} role="alert">
      <div className="toast-content">
        {type === 'success' && <span className="toast-icon">✓</span>}
        {type === 'error' && <span className="toast-icon">✕</span>}
        {type === 'warning' && <span className="toast-icon">⚠</span>}
        {type === 'info' && <span className="toast-icon">ℹ</span>}
        <span className="toast-message">{message}</span>
      </div>
      <button
        className="toast-close"
        onClick={() => onClose(id)}
        aria-label="Close toast"
      >
        ×
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type, options = {}) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts(prev => [...prev, { id, message, type }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showSuccess = useCallback((message, options) => addToast(message, 'success', options), [addToast]);
  const showError = useCallback((message, options) => addToast(message, 'error', options), [addToast]);
  const showWarning = useCallback((message, options) => addToast(message, 'warning', options), [addToast]);
  const showInfo = useCallback((message, options) => addToast(message, 'info', options), [addToast]);

  React.useEffect(() => {
    registerToastCallbacks({
      success: showSuccess,
      error: showError,
      warning: showWarning,
      info: showInfo,
    });
  }, [showSuccess, showError, showWarning, showInfo]);

  return (
    <>
      {children}
      <div className="toast-container" role="region" aria-live="polite" aria-atomic="true">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={removeToast}
          />
        ))}
      </div>
    </>
  );
};

export default ToastProvider;
